import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Pencil,
  Trash2,
  Users as UsersIcon,
  Building2,
  FileText,
  ExternalLink,
} from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";
import FileUploader from "@/components/FileUploader";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const emptyMember = { name: "", department: "", designation: "" };
const emptyForm = {
  name: "",
  objective_html: "",
  has_functions: false,
  functions_html: "",
  members: [{ ...emptyMember }],
  remarks: "",
  has_pdf: false,
  pdf_url: "",
  pdf_public_id: "",
  status: "Active",
};

function Field({ label, children, full, hint }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
        {label}
      </div>
      {children}
      {hint ? <div className="mt-1 text-[11px] text-[#6b655b]">{hint}</div> : null}
    </label>
  );
}

export default function Committees() {
  const { role } = useAuth();
  const canEdit = role === "institute_admin";

  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [viewing, setViewing] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cs, tps] = await Promise.all([
        api.get("/institute/committees"),
        api.get("/masters/committee_type"),
      ]);
      setItems(cs.data);
      setTypes(tps.data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const visible = useMemo(() => {
    return items.filter((it) => (statusFilter ? it.status === statusFilter : true));
  }, [items, statusFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, members: [{ ...emptyMember }] });
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "",
      objective_html: c.objective_html || "",
      has_functions: !!c.has_functions,
      functions_html: c.functions_html || "",
      members:
        (c.members || []).length > 0
          ? c.members.map((m) => ({
              name: m.name || "",
              department: m.department || "",
              designation: m.designation || "",
            }))
          : [{ ...emptyMember }],
      remarks: c.remarks || "",
      has_pdf: !!c.has_pdf,
      pdf_url: c.pdf_url || "",
      pdf_public_id: c.pdf_public_id || "",
      status: c.status || "Active",
    });
    setOpen(true);
  };

  const setM = (idx, key, value) =>
    setForm((f) => {
      const members = f.members.slice();
      members[idx] = { ...members[idx], [key]: value };
      return { ...f, members };
    });

  const addMemberRow = () =>
    setForm((f) => ({ ...f, members: [...f.members, { ...emptyMember }] }));

  const removeMemberRow = (idx) =>
    setForm((f) => {
      const next = f.members.slice();
      next.splice(idx, 1);
      return { ...f, members: next.length === 0 ? [{ ...emptyMember }] : next };
    });

  const save = async () => {
    if (!form.name) {
      toast.error("Please select a Committee/Cell name");
      return;
    }
    if (form.has_pdf && !form.pdf_url) {
      toast.error("Please upload a PDF or uncheck the attachment option");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        // Drop empty member rows on the client too (backend re-validates)
        members: form.members.filter((m) => (m.name || "").trim()),
      };
      if (editing) {
        const { data } = await api.put(`/institute/committees/${editing.id}`, payload);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
        toast.success("Committee updated");
      } else {
        const { data } = await api.post("/institute/committees", payload);
        setItems((arr) => [data, ...arr]);
        toast.success("Committee created");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const c = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/institute/committees/${c.id}`);
      setItems((arr) => arr.filter((x) => x.id !== c.id));
      toast.success("Committee removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-6xl mx-auto" data-testid="committees-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Governance</div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Committees &amp; Cells</h1>
          <p className="mt-2 text-sm text-[#6b655b] max-w-2xl">
            Constitute statutory and functional committees for your institute — Anti-Ragging,
            IQAC, ICC, Grievance Redressal and more. Names are managed under Trust Masters.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={openCreate}
            disabled={types.length === 0}
            data-testid="committees-add-button"
            className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm disabled:opacity-60"
          >
            <Plus size={14} strokeWidth={1.5} /> Add Committee
          </button>
        )}
      </div>

      <div className="flex gap-2 flex-wrap mb-6">
        <select
          data-testid="committees-filter-status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className={inputCls + " w-auto"}
        >
          <option value="">All statuses</option>
          <option value="Active">Active only</option>
          <option value="Inactive">Inactive only</option>
        </select>
        <div className="ml-auto text-sm text-[#6b655b] self-center">
          {visible.length} of {items.length}
        </div>
      </div>

      {loading ? (
        <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
      ) : visible.length === 0 ? (
        <div
          className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-16 text-center"
          data-testid="committees-empty"
        >
          <Building2 size={32} strokeWidth={1.5} className="mx-auto text-[#234333]" />
          <div className="mt-4 font-serif text-2xl text-[#1c1a17]">
            {items.length === 0 ? "No committees yet" : "No matches for this filter"}
          </div>
          <div className="mt-2 text-sm text-[#6b655b]">
            {items.length === 0 && canEdit
              ? `Click "Add Committee" to constitute your first one.`
              : items.length === 0
                ? "Your Institute Admin hasn't constituted any yet."
                : "Try changing the status filter above."}
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="committees-list">
          {visible.map((c) => (
            <article
              key={c.id}
              data-testid={`committees-card-${c.id}`}
              className="bg-white border border-[#e5e0d8] rounded-sm p-6"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-serif text-2xl text-[#1c1a17] leading-tight">{c.name}</h2>
                    <span
                      className={`inline-block text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm border ${
                        c.status === "Active"
                          ? "border-[#234333]/40 text-[#234333] bg-[#234333]/5"
                          : "border-[#9e2a2b]/40 text-[#9e2a2b] bg-[#9e2a2b]/5"
                      }`}
                    >
                      {c.status}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-[#6b655b] uppercase tracking-wide">
                    <UsersIcon size={12} strokeWidth={1.5} />
                    {(c.members || []).length} member
                    {(c.members || []).length === 1 ? "" : "s"}
                    {c.has_functions ? " · with functions listed" : ""}
                    {c.has_pdf && c.pdf_url ? (
                      <a
                        href={c.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`committees-pdf-link-${c.id}`}
                        className="ml-2 inline-flex items-center gap-1 normal-case tracking-normal text-[#234333] hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FileText size={12} strokeWidth={1.5} /> View PDF
                        <ExternalLink size={10} strokeWidth={1.5} />
                      </a>
                    ) : null}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setViewing(c)}
                    data-testid={`committees-view-${c.id}`}
                    className="text-xs text-[#234333] hover:bg-[#efece6] px-3 py-1.5 rounded-sm border border-[#e5e0d8]"
                  >
                    View
                  </button>
                  {canEdit && (
                    <>
                      <button
                        onClick={() => openEdit(c)}
                        data-testid={`committees-edit-${c.id}`}
                        className="inline-flex items-center gap-1 text-xs text-[#234333] hover:bg-[#efece6] px-3 py-1.5 rounded-sm border border-[#e5e0d8]"
                      >
                        <Pencil size={12} strokeWidth={1.5} /> Edit
                      </button>
                      <button
                        onClick={() => setConfirmDelete(c)}
                        data-testid={`committees-delete-${c.id}`}
                        className="inline-flex items-center gap-1 text-xs text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-3 py-1.5 rounded-sm border border-[#9e2a2b]/30"
                      >
                        <Trash2 size={12} strokeWidth={1.5} /> Delete
                      </button>
                    </>
                  )}
                </div>
              </div>

              {c.objective_html && (
                <div className="mt-4">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1">
                    Objective
                  </div>
                  <div
                    data-testid={`committees-objective-${c.id}`}
                    className="prose prose-sm max-w-none text-[#1c1a17] text-sm line-clamp-3"
                    dangerouslySetInnerHTML={{ __html: c.objective_html }}
                  />
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-4xl max-h-[92vh] overflow-y-auto"
          data-testid="committees-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {editing ? "Edit Committee / Cell" : "Constitute Committee / Cell"}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#6b655b]">
              Pick a name from the master list, describe the objective and functions, and add
              the members.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
            <Field label="Name of Committee / Cell *">
              <select
                data-testid="committees-name-select"
                className={inputCls}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              >
                <option value="">— Select —</option>
                {types.map((t) => (
                  <option key={t.id} value={t.name}>
                    {t.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Status *">
              <select
                data-testid="committees-status-select"
                className={inputCls}
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </Field>

            <Field
              label="Objective of the Committee / Cell"
              full
              hint="Supports formatting, lists, headings, links and text alignment."
            >
              <RichTextEditor
                testid="committees-objective-editor"
                value={form.objective_html}
                onChange={(html) => setForm((f) => ({ ...f, objective_html: html }))}
                placeholder="Why was this committee constituted? What outcomes is it accountable for?"
              />
            </Field>

            <div className="md:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="committees-has-functions"
                  checked={form.has_functions}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, has_functions: e.target.checked }))
                  }
                  className="w-4 h-4 accent-[#234333]"
                />
                <span className="text-sm text-[#1c1a17]">
                  This committee has documented functions / responsibilities
                </span>
              </label>
            </div>

            {form.has_functions && (
              <Field
                label="Functions of the Committee / Cell"
                full
                hint="List specific duties, meeting frequency, reporting line, etc."
              >
                <RichTextEditor
                  testid="committees-functions-editor"
                  value={form.functions_html}
                  onChange={(html) => setForm((f) => ({ ...f, functions_html: html }))}
                  placeholder="Use bullet points or numbered list for individual responsibilities."
                />
              </Field>
            )}

            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
                  Members
                </div>
                <button
                  type="button"
                  onClick={addMemberRow}
                  data-testid="committees-add-member"
                  className="inline-flex items-center gap-1 text-xs text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm border border-[#e5e0d8]"
                >
                  <Plus size={12} strokeWidth={1.5} /> Add Row
                </button>
              </div>

              <div className="overflow-x-auto border border-[#e5e0d8] rounded-sm">
                <table className="w-full text-sm">
                  <thead className="bg-[#f9f8f6] border-b border-[#e5e0d8]">
                    <tr>
                      <th className="text-left px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
                        #
                      </th>
                      <th className="text-left px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
                        Name *
                      </th>
                      <th className="text-left px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
                        Department / Portfolio
                      </th>
                      <th className="text-left px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
                        Designation
                      </th>
                      <th className="px-3 py-2 w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {form.members.map((m, idx) => (
                      <tr
                        key={idx}
                        data-testid={`committees-member-row-${idx}`}
                        className="border-t border-[#e5e0d8]"
                      >
                        <td className="px-3 py-2 text-xs text-[#6b655b]">{idx + 1}</td>
                        <td className="px-3 py-2">
                          <input
                            data-testid={`committees-member-name-${idx}`}
                            className={inputCls + " py-1.5"}
                            placeholder="e.g., Dr. A. K. Sharma"
                            maxLength={200}
                            value={m.name}
                            onChange={(e) => setM(idx, "name", e.target.value)}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            data-testid={`committees-member-dept-${idx}`}
                            className={inputCls + " py-1.5"}
                            placeholder="e.g., Computer Science"
                            value={m.department}
                            onChange={(e) => setM(idx, "department", e.target.value)}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            data-testid={`committees-member-desig-${idx}`}
                            className={inputCls + " py-1.5"}
                            placeholder="e.g., Convenor / Member"
                            value={m.designation}
                            onChange={(e) => setM(idx, "designation", e.target.value)}
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => removeMemberRow(idx)}
                            data-testid={`committees-member-remove-${idx}`}
                            title="Remove row"
                            className="text-[#9e2a2b] hover:bg-[#9e2a2b]/5 p-1.5 rounded-sm"
                          >
                            <Trash2 size={12} strokeWidth={1.5} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-1 text-[11px] text-[#6b655b]">
                Empty rows are dropped on save.
              </div>
            </div>

            <Field label="Remarks" full hint="Optional — anything else worth recording.">
              <textarea
                data-testid="committees-remarks-input"
                className={inputCls + " min-h-[80px] resize-y"}
                placeholder="e.g., Reports submitted yearly to the Trust, last reconstituted on 1-Jul-2024."
                value={form.remarks}
                onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))}
              />
            </Field>

            <div className="md:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="committees-has-pdf"
                  checked={form.has_pdf}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      has_pdf: e.target.checked,
                      // Reset url/pid when unchecking
                      pdf_url: e.target.checked ? f.pdf_url : "",
                      pdf_public_id: e.target.checked ? f.pdf_public_id : "",
                    }))
                  }
                  className="w-4 h-4 accent-[#234333]"
                />
                <span className="text-sm text-[#1c1a17]">
                  Any PDF to be attached? (e.g., constitution, minutes, annual report)
                </span>
              </label>
            </div>

            {form.has_pdf && (
              <div className="md:col-span-2">
                <FileUploader
                  kind="inst_committee_pdf"
                  label="PDF Attachment"
                  url={form.pdf_url}
                  testid="committees-pdf"
                  onChange={({ url, public_id }) =>
                    setForm((f) => ({ ...f, pdf_url: url, pdf_public_id: public_id }))
                  }
                />
              </div>
            )}
          </div>

          <DialogFooter className="pt-4">
            <button
              onClick={() => setOpen(false)}
              data-testid="committees-cancel-button"
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="committees-save-button"
              className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto"
          data-testid="committees-view-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {viewing?.name}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#6b655b]">
              Read-only view of the committee record.
            </DialogDescription>
          </DialogHeader>

          {viewing && (
            <div className="space-y-5 py-2">
              {viewing.objective_html && (
                <section>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                    Objective
                  </div>
                  <div
                    className="prose prose-sm max-w-none text-[#1c1a17]"
                    dangerouslySetInnerHTML={{ __html: viewing.objective_html }}
                  />
                </section>
              )}

              {viewing.has_functions && viewing.functions_html && (
                <section>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                    Functions
                  </div>
                  <div
                    className="prose prose-sm max-w-none text-[#1c1a17]"
                    dangerouslySetInnerHTML={{ __html: viewing.functions_html }}
                  />
                </section>
              )}

              <section>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                  Members ({(viewing.members || []).length})
                </div>
                {(viewing.members || []).length === 0 ? (
                  <div className="text-sm text-[#6b655b] italic">No members listed.</div>
                ) : (
                  <div className="overflow-x-auto border border-[#e5e0d8] rounded-sm">
                    <table className="w-full text-sm">
                      <thead className="bg-[#f9f8f6] border-b border-[#e5e0d8]">
                        <tr>
                          <th className="text-left px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
                            #
                          </th>
                          <th className="text-left px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
                            Name
                          </th>
                          <th className="text-left px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
                            Department / Portfolio
                          </th>
                          <th className="text-left px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
                            Designation
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewing.members.map((m, i) => (
                          <tr key={i} className="border-t border-[#e5e0d8]">
                            <td className="px-3 py-2 text-xs text-[#6b655b]">{i + 1}</td>
                            <td className="px-3 py-2 text-[#1c1a17]">{m.name}</td>
                            <td className="px-3 py-2 text-[#6b655b]">{m.department || "—"}</td>
                            <td className="px-3 py-2 text-[#6b655b]">{m.designation || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {viewing.remarks && (
                <section>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                    Remarks
                  </div>
                  <div className="text-sm text-[#1c1a17] whitespace-pre-line">
                    {viewing.remarks}
                  </div>
                </section>
              )}

              {viewing.has_pdf && viewing.pdf_url && (
                <section>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                    Attached PDF
                  </div>
                  <a
                    href={viewing.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="committees-view-pdf-link"
                    className="inline-flex items-center gap-2 text-sm text-[#234333] hover:underline px-3 py-2 border border-[#e5e0d8] rounded-sm bg-[#f9f8f6]"
                  >
                    <FileText size={14} strokeWidth={1.5} /> Open attached PDF
                    <ExternalLink size={11} strokeWidth={1.5} />
                  </a>
                </section>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-white border-[#e5e0d8] rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Delete this committee?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              "{confirmDelete?.name}" and its member list will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-[#1c1a17] border-[#e5e0d8] rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="committees-delete-confirm"
              className="bg-[#9e2a2b] hover:bg-[#7d2122] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
